import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import SeoHead from '../SeoHead';
import { DEFAULT_SITE_NAME } from '../../constants/brand';
import { CANONICAL_ORIGIN } from '../../constants/site';
import { buildGameJsonLd, getGamePageContent } from '../../content/gameInfo';
import { getSeoKeywords } from '../../content/seo';
import { guideService } from '../../services/api';
import type { GuideTreeCategory } from '../../types';
import { getBuiltInGuideCategories, cmsSlugOverlapsBuiltIn } from '../../content/gameGuideBuiltIn';
import { guideText } from '../../utils/guideLocale';
import '../../styles/Game.css';

const GameGuideLayout: React.FC = () => {
    const { locale } = useLanguage();
    const { pathname } = useLocation();
    const { slug } = useParams<{ slug?: string }>();
    const seoPath = pathname.startsWith('/game') ? pathname : '/game';
    const { config } = useSiteConfig();
    const content = getGamePageContent(locale);
    const siteName = config.general?.siteName?.trim() || DEFAULT_SITE_NAME;
    const [tree, setTree] = useState<GuideTreeCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await guideService.getTree();
                setTree(Array.isArray(data) ? data : []);
            } catch {
                setTree([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        setSidebarOpen(false);
    }, [slug]);

    const jsonLd = React.useMemo(
        () => buildGameJsonLd(siteName, CANONICAL_ORIGIN, locale),
        [siteName, locale]
    );

    const builtInCategories = React.useMemo(() => getBuiltInGuideCategories(locale), [locale]);

    const cmsTree = React.useMemo(
        () =>
            tree
                .map((cat) => ({
                    ...cat,
                    articles: cat.articles.filter((a) => !cmsSlugOverlapsBuiltIn(a.slug)),
                }))
                .filter((cat) => cat.articles.length > 0),
        [tree]
    );

    const labels =
        locale === 'pt-BR'
            ? {
                  menu: 'Menu do guia',
                  home: 'Início do guia',
                  loading: 'Carregando guias...',
                  more: 'Mais tutoriais',
              }
            : {
                  menu: 'Guide menu',
                  home: 'Guide home',
                  loading: 'Loading guides...',
                  more: 'More tutorials',
              };

    return (
        <div className="game-page">
            <SeoHead
                title={content.seo.title}
                description={content.seo.description}
                path={seoPath}
                keywords={getSeoKeywords(locale)}
                jsonLd={jsonLd as unknown as Record<string, unknown>[]}
            />

            <header
                className="game-hero game-hero--compact"
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ''}/assets/img/ridersoficarus.jpg)` }}
            >
                <div className="game-hero-overlay" />
                <div className="container game-hero-inner">
                    <span className="game-hero-badge">
                        <i className="ri-book-open-line" /> {content.hero.badge}
                    </span>
                    <h1>
                        {content.hero.title}
                        <span className="highlight"> & {content.hero.highlight}</span>
                    </h1>
                    <p>{content.hero.subtitle}</p>
                    <div className="game-hero-actions">
                        <Link to="/download" className="btn btn-primary">
                            <i className="ri-download-cloud-2-line" /> {content.cta.download}
                        </Link>
                        <Link to="/classes" className="btn btn-outline">
                            <i className="ri-sword-line" /> {content.classesTeaser.cta}
                        </Link>
                    </div>
                </div>
            </header>

            <div className="container game-layout">
                <button
                    type="button"
                    className="game-sidebar-toggle"
                    onClick={() => setSidebarOpen((o) => !o)}
                    aria-expanded={sidebarOpen}
                >
                    <i className="ri-menu-line" /> {labels.menu}
                </button>

                <aside className={`game-sidebar${sidebarOpen ? ' is-open' : ''}`}>
                    <nav aria-label={labels.menu}>
                        <NavLink to="/game" end className="game-sidebar-home">
                            <i className="ri-home-4-line" /> {labels.home}
                        </NavLink>

                        <NavLink to="/game/montarias" className="game-sidebar-mount-guide">
                            <i className="ri-dragon-line" />{' '}
                            {locale === 'pt-BR' ? 'Guia de montarias' : 'Mount guide'}
                        </NavLink>

                        {builtInCategories.map((cat) => (
                            <div key={cat.key} className="game-sidebar-group">
                                <p className="game-sidebar-group-title">{cat.title}</p>
                                <ul>
                                    {cat.topics.map((topic) => (
                                        <li key={topic.slug}>
                                            <NavLink
                                                to={topic.href ?? `/game/${topic.slug}`}
                                                className={({ isActive }) => (isActive ? 'active' : '')}
                                            >
                                                {topic.title}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        {loading && cmsTree.length === 0 && (
                            <p className="game-sidebar-muted">{labels.loading}</p>
                        )}

                        {cmsTree.map((cat) => (
                            <div key={cat.id} className="game-sidebar-group">
                                <p className="game-sidebar-group-title">
                                    {labels.more}: {guideText(locale, cat.titleEn, cat.titlePtBr)}
                                </p>
                                <ul>
                                    {cat.articles.map((art) => (
                                        <li key={art.id}>
                                            <NavLink
                                                to={`/game/${art.slug}`}
                                                className={({ isActive }) => (isActive ? 'active' : '')}
                                            >
                                                {guideText(locale, art.titleEn, art.titlePtBr)}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </aside>

                <div className="game-main">
                    <Outlet context={{ tree, loading, content }} />
                </div>
            </div>
        </div>
    );
};

export default GameGuideLayout;
